import { useCallback, useEffect, useRef, useState } from 'react';

import { getFileExtension } from '../../utils/formatters';

const ALLOWED_UPLOAD_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'heic']);
const ALLOWED_UPLOAD_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/heif']);

const isHeicFile = (file) => {
  const extension = getFileExtension(file?.name || '');
  const type = String(file?.type || '').toLowerCase();
  return extension === 'heic' || extension === 'heif' || type.includes('heic') || type.includes('heif');
};

const isAllowedUploadFile = (file) => {
  const extension = getFileExtension(file?.name || '');
  const mimeType = String(file?.type || '').toLowerCase();
  if (extension) {
    return ALLOWED_UPLOAD_EXTENSIONS.has(extension);
  }
  return ALLOWED_UPLOAD_MIME_TYPES.has(mimeType);
};

const splitUploadFilesByType = (files = []) => {
  const valid = [];
  const invalid = [];
  files.forEach((file) => {
    if (isAllowedUploadFile(file)) {
      valid.push(file);
    } else {
      invalid.push(file);
    }
  });
  return { valid, invalid };
};

const alertInvalidFiles = (invalidFiles = []) => {
  if (!invalidFiles.length) return;
  const invalidNames = invalidFiles
    .map((file) => file?.name || 'unknown file')
    .slice(0, 8)
    .join(', ');
  const suffix = invalidFiles.length > 8 ? ', ...' : '';
  alert(`Only JPG, PNG, and HEIC files are allowed.\nIgnored: ${invalidNames}${suffix}`);
};

const buildPreviewId = (file) => `${Date.now()}-${file?.name || 'image'}-${Math.random().toString(36).slice(2, 8)}`;

const buildPreview = (file) => {
  const extension = getFileExtension(file?.name || '');
  const heic = isHeicFile(file);
  return {
    id: buildPreviewId(file),
    url: URL.createObjectURL(file),
    name: file?.name || 'image',
    extension,
    isHeic: heic,
    isConverting: heic,
    hasPreviewError: false,
  };
};

export const useAnalysisUploads = ({ onFilesChanged }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef([]);
  const heicConverterPromiseRef = useRef(null);

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((preview) => {
        if (preview?.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, []);

  const getHeicConverter = useCallback(async () => {
    if (!heicConverterPromiseRef.current) {
      heicConverterPromiseRef.current = import('heic2any').then((heic2anyModule) => (
        heic2anyModule.default || heic2anyModule
      ));
    }
    return heicConverterPromiseRef.current;
  }, []);

  const convertHeicToPreviewBlob = useCallback(async (file) => {
    const heic2any = await getHeicConverter();
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    });
    if (Array.isArray(converted)) {
      return converted[0];
    }
    return converted;
  }, [getHeicConverter]);

  const convertHeicPreview = useCallback(async (previewId, file) => {
    try {
      const convertedBlob = await convertHeicToPreviewBlob(file);
      if (!(convertedBlob instanceof Blob)) {
        throw new Error('HEIC conversion returned no blob');
      }
      const convertedUrl = URL.createObjectURL(convertedBlob);
      setPreviewUrls((prev) => {
        let matched = false;
        const next = prev.map((preview) => {
          if (preview.id !== previewId) return preview;
          matched = true;
          if (preview.url && preview.url !== convertedUrl) {
            URL.revokeObjectURL(preview.url);
          }
          return {
            ...preview,
            url: convertedUrl,
            isConverting: false,
            hasPreviewError: false,
          };
        });
        if (!matched) {
          URL.revokeObjectURL(convertedUrl);
        }
        return next;
      });
    } catch (error) {
      console.warn(`Failed to convert ${file?.name || 'HEIC file'} for preview:`, error);
      setPreviewUrls((prev) => prev.map((preview) => (
        preview.id === previewId
          ? { ...preview, isConverting: false }
          : preview
      )));
    }
  }, [convertHeicToPreviewBlob]);

  const appendFiles = useCallback((files) => {
    setSelectedFiles((prev) => [...prev, ...files]);
    const newPreviews = files.map((file) => buildPreview(file));
    setPreviewUrls((prev) => [...prev, ...newPreviews]);
    if (onFilesChanged) {
      onFilesChanged();
    }
    newPreviews.forEach((preview, index) => {
      if (preview.isHeic) {
        convertHeicPreview(preview.id, files[index]);
      }
    });
  }, [convertHeicPreview, onFilesChanged]);

  const processIncomingFiles = useCallback((files) => {
    const { valid, invalid } = splitUploadFilesByType(files);
    alertInvalidFiles(invalid);
    if (valid.length > 0) {
      appendFiles(valid);
    }
  }, [appendFiles]);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    processIncomingFiles(files);
    e.target.value = '';
  }, [processIncomingFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    processIncomingFiles(files);
  }, [processIncomingFiles]);

  const markPreviewError = useCallback((index) => {
    setPreviewUrls((prev) => prev.map((preview, i) => (
      i === index ? { ...preview, hasPreviewError: true } : preview
    )));
  }, []);

  const removeFile = useCallback((index) => {
    setPreviewUrls((prev) => {
      const previewToRemove = prev[index];
      if (previewToRemove?.url) {
        URL.revokeObjectURL(previewToRemove.url);
      }
      return prev.filter((_, i) => i !== index);
    });
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (onFilesChanged) {
      onFilesChanged();
    }
  }, [onFilesChanged]);

  return {
    selectedFiles,
    previewUrls,
    isDragOver,
    fileInputRef,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    markPreviewError,
    removeFile,
    setPreviewUrls,
  };
};
